import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useClearHistoryAgentTestClearHistoryPostMutation,
  useGetMessageListAgentTestListGetQuery,
  useSendAgentMessageAgentTestMessagePostMutation,
} from '@shared/api';
import { agentTestEvents, type AgentTestSwitchPayload } from '@shared/services/agent-test';

import type { UiMessage } from '../lib/types';
import { extractRequestId, isAiLikeCreatorType, normalizeMessageText } from '../lib/utils';
import { useAgentTestStream } from './use-agent-test-stream';

const LONG_IDLE_RESET_MS = 5 * 60_000;
const CHANGE_CURRENT_AGENT_APPLY_WINDOW_MS = 5_000;

type UseAgentTestMessagesParams = {
  agentId: number;
  onAfterClear?: () => void;
};

type PendingSwitchState = {
  newAgentId: number;
  minMessageIdExclusive: number;
  messageId?: number;
};

const getMessageOrderId = (message: UiMessage) => {
  if (typeof message.id === 'number') return message.id;
  if (typeof message.requestId === 'number') return message.requestId + 0.5;
  return Number.MAX_SAFE_INTEGER;
};

const compareByOrderId = (a: UiMessage, b: UiMessage) => {
  return getMessageOrderId(a) - getMessageOrderId(b);
};

const getMaxNumericMessageId = (messages: UiMessage[]) => {
  let max = 0;

  messages.forEach((message) => {
    if (typeof message.id !== 'number') return;
    if (message.id > max) max = message.id;
  });

  return max;
};

const resolveSwitchTargetMessageId = ({
  messages,
  minMessageIdExclusive,
  explicitMessageId,
}: {
  messages: UiMessage[];
  minMessageIdExclusive?: number;
  explicitMessageId?: number;
}) => {
  if (typeof explicitMessageId === 'number') {
    const explicitMessage = messages.find(
      (message) => message.id === explicitMessageId && message.creator_type === 'USER',
    );

    if (explicitMessage) return explicitMessageId;
  }

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];

    if (message.creator_type !== 'USER') continue;
    if (typeof message.id !== 'number') continue;
    if (typeof minMessageIdExclusive === 'number' && message.id <= minMessageIdExclusive) continue;

    return message.id;
  }

  return null;
};

export const useAgentTestMessages = ({ agentId, onAfterClear }: UseAgentTestMessagesParams) => {
  const [inputValue, setInputValue] = useState('');
  const [sendScrollSignal, setSendScrollSignal] = useState(0);
  const [hideRecommendations, setHideRecommendations] = useState(false);
  const [isSendLocked, setIsSendLocked] = useState(false);
  const [switchOverridesByMessageId, setSwitchOverridesByMessageId] = useState<
    Record<number, number>
  >({});
  const [switchAgentNamesById, setSwitchAgentNamesById] = useState<Record<number, string>>({});
  const sendInFlightRef = useRef(false);
  const hiddenAtRef = useRef<number | null>(null);
  const pendingSwitchRef = useRef<PendingSwitchState | null>(null);
  const lastScopedSwitchAtRef = useRef<number | null>(null);
  const messagesRef = useRef<UiMessage[]>([]);
  const switchOverridesRef = useRef<Record<number, number>>({});
  const { streamMessages, registerRequestId, reset, resetPending } = useAgentTestStream({ agentId });

  const { data, isLoading, isFetching } = useGetMessageListAgentTestListGetQuery(
    { agentId, limit: 50, offset: 0 },
    {
      skip: !agentId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const [sendMessage, { isLoading: isSending }] = useSendAgentMessageAgentTestMessagePostMutation();
  const [clearHistory, { isLoading: isClearing }] =
    useClearHistoryAgentTestClearHistoryPostMutation();

  useEffect(() => {
    switchOverridesRef.current = switchOverridesByMessageId;
  }, [switchOverridesByMessageId]);

  useEffect(() => {
    messagesRef.current = [];
    switchOverridesRef.current = {};
    pendingSwitchRef.current = null;
    lastScopedSwitchAtRef.current = null;
    setSwitchOverridesByMessageId({});
    setSwitchAgentNamesById({});
  }, [agentId]);

  const applySwitchOverride = useCallback((messageId: number, newAgentId: number) => {
    setSwitchOverridesByMessageId((prev) => {
      if (prev[messageId] === newAgentId) return prev;
      return {
        ...prev,
        [messageId]: newAgentId,
      };
    });
  }, []);

  const handleSwitchAgentEvent = useCallback(
    (payload: AgentTestSwitchPayload) => {
      const newAgentId = Number(payload.newAgentId);
      if (!Number.isFinite(newAgentId)) return;

      const payloadAgentId = Number(payload.agentId);
      const hasPayloadAgentId = Number.isFinite(payloadAgentId);

      if (payload.source === 'AGENT_TEST_SWITCH_AGENT') {
        if (hasPayloadAgentId && payloadAgentId !== agentId) return;
        lastScopedSwitchAtRef.current = Date.now();
      } else {
        const lastScopedSwitchAt = lastScopedSwitchAtRef.current;
        if (!lastScopedSwitchAt) return;
        if (Date.now() - lastScopedSwitchAt > CHANGE_CURRENT_AGENT_APPLY_WINDOW_MS) return;
      }

      if (payload.newAgentName?.trim()) {
        const trimmedName = payload.newAgentName.trim();

        setSwitchAgentNamesById((prev) => {
          if (prev[newAgentId] === trimmedName) return prev;
          return {
            ...prev,
            [newAgentId]: trimmedName,
          };
        });
      }

      const explicitMessageId = Number(payload.messageId);
      const hasExplicitMessageId = Number.isFinite(explicitMessageId);
      const currentMessages = messagesRef.current;
      const maxMessageId = getMaxNumericMessageId(currentMessages);

      const immediateTargetMessageId = resolveSwitchTargetMessageId({
        messages: currentMessages,
        explicitMessageId: hasExplicitMessageId ? explicitMessageId : undefined,
      });

      if (immediateTargetMessageId !== null) {
        applySwitchOverride(immediateTargetMessageId, newAgentId);
        pendingSwitchRef.current = null;
        return;
      }

      pendingSwitchRef.current = {
        newAgentId,
        minMessageIdExclusive: maxMessageId,
        messageId: hasExplicitMessageId ? explicitMessageId : undefined,
      };
    },
    [agentId, applySwitchOverride],
  );

  useEffect(() => {
    if (!agentId) return;

    const unsubscribeSwitchAgent = agentTestEvents.onSwitchAgent(handleSwitchAgentEvent);

    return () => {
      unsubscribeSwitchAgent();
    };
  }, [agentId, handleSwitchAgentEvent]);

  const historyMessages = useMemo<UiMessage[]>(() => {
    const items = data?.items ?? [];
    const deduped = new Map<number, UiMessage>();

    items.forEach((item) => {
      if (deduped.has(item.id)) return;

      deduped.set(item.id, {
        id: item.id,
        creator_type: item.creator_type,
        message: normalizeMessageText(item.message),
        created_at: item.created_at,
        recommendations: item.recommendations,
        switched_agent_id: switchOverridesByMessageId[item.id] ?? item.switched_agent_id ?? null,
      });
    });

    return [...deduped.values()].sort(compareByOrderId);
  }, [data?.items, switchOverridesByMessageId]);

  const historyIds = useMemo(
    () => new Set(historyMessages.map((message) => Number(message.id))),
    [historyMessages],
  );

  const historyRequestIds = useMemo(() => {
    const ids = new Set<number>();
    historyMessages.forEach((message) => {
      const requestId = extractRequestId(message.message);
      if (!requestId) return;
      ids.add(requestId);
    });
    return ids;
  }, [historyMessages]);

  const visibleStreamMessages = useMemo(
    () =>
      streamMessages.filter(
        (message) =>
          (!message.finalMessageId || !historyIds.has(message.finalMessageId)) &&
          !historyRequestIds.has(message.requestId),
      ),
    [historyIds, historyRequestIds, streamMessages],
  );

  const messages = useMemo(() => {
    const deduped = new Map<string | number, UiMessage>();

    [...historyMessages, ...visibleStreamMessages].forEach((message) => {
      deduped.set(message.id, message);
    });

    return [...deduped.values()].sort(compareByOrderId);
  }, [historyMessages, visibleStreamMessages]);

  useEffect(() => {
    messagesRef.current = messages;

    const pendingSwitch = pendingSwitchRef.current;
    if (!pendingSwitch) return;

    const targetMessageId = resolveSwitchTargetMessageId({
      messages,
      minMessageIdExclusive:
        pendingSwitch.messageId !== undefined ? undefined : pendingSwitch.minMessageIdExclusive,
      explicitMessageId: pendingSwitch.messageId,
    });

    if (targetMessageId === null) return;

    applySwitchOverride(targetMessageId, pendingSwitch.newAgentId);
    pendingSwitchRef.current = null;
  }, [messages, applySwitchOverride]);

  const recommendations = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (!isAiLikeCreatorType(message.creator_type)) continue;
      if (message.recommendations?.length) return message.recommendations;
    }
    return [];
  }, [messages]);

  const recommendationsMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (!isAiLikeCreatorType(message.creator_type)) continue;
      if (message.recommendations?.length) return message.id;
    }
    return null;
  }, [messages]);

  useEffect(() => {
    setHideRecommendations(false);
  }, [recommendationsMessageId]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }

      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (!hiddenAt) return;

      if (Date.now() - hiddenAt >= LONG_IDLE_RESET_MS) {
        resetPending();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [resetPending]);

  const handleSendMessage = useCallback(
    async (value: string) => {
      const message = value.trim();
      if (!message || !agentId) return false;
      if (sendInFlightRef.current) return false;

      sendInFlightRef.current = true;
      setHideRecommendations(true);
      setIsSendLocked(true);
      setSendScrollSignal((prev) => prev + 1);

      try {
        const data = await sendMessage({
          agentId,
          bodySendAgentMessageAgentTestMessagePost: { message },
        }).unwrap();

        if (data?.id) {
          registerRequestId(data.id);
        }

        setInputValue('');
        return true;
      } catch {
        // errors handled globally
        setHideRecommendations(false);
        return false;
      } finally {
        sendInFlightRef.current = false;
        setIsSendLocked(false);
      }
    },
    [agentId, registerRequestId, sendMessage],
  );

  const handleClearHistory = useCallback(async () => {
    if (!agentId) return false;

    try {
      await clearHistory({ agentId }).unwrap();
      reset();
      setInputValue('');
      setSwitchOverridesByMessageId({});
      setSwitchAgentNamesById({});
      switchOverridesRef.current = {};
      pendingSwitchRef.current = null;
      lastScopedSwitchAtRef.current = null;
      onAfterClear?.();
      return true;
    } catch {
      // errors handled globally
      return false;
    }
  }, [agentId, clearHistory, onAfterClear, reset]);

  const handleSendRecommendation = useCallback(
    async (value: string) => {
      if (sendInFlightRef.current) return;
      await handleSendMessage(value);
    },
    [handleSendMessage],
  );

  return {
    inputValue,
    setInputValue,
    messages,
    sendScrollSignal,
    recommendations: hideRecommendations ? [] : recommendations,
    handleSendMessage,
    handleSendRecommendation,
    handleClearHistory,
    isSending: isSending || isSendLocked,
    isRecommendationsLocked: hideRecommendations || isSending || isSendLocked,
    isClearing,
    isLoading,
    isFetching,
    switchAgentNamesById,
  };
};
